import {
  BarChart3,
  Crosshair,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DetailNetBenchmarkChart from "../dashboard/components/DetailNetBenchmarkChart";
import { DEFAULT_BENCHMARK_SYMBOL, getBenchmarkBySymbol } from "../../config/benchmarkContracts";
import {
  formatCurrencyWithCommas,
  formatPercentage,
  formatSignedPercentage,
} from "../../utils/numberUtils";
import { openAlphaRiskProfileUpdate } from "./alphaHistory";
import { useAlphaWhyManagedData } from "./alphaWhyManagedData";

const ALPHA_BENCHMARK = getBenchmarkBySymbol(DEFAULT_BENCHMARK_SYMBOL);
const CHART_HEIGHT = 260;

// Existing Plutus Managed product language, restored verbatim from the prior
// experience so the recommendation keeps its established Argento pitch.
const WHY_REASONS: ReadonlyArray<{
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  body: string;
}> = [
  {
    icon: Crosshair,
    title: "Matched to you",
    body: "Built from your goals and risk, and right-sized to what you invest.",
  },
  {
    icon: BarChart3,
    title: "Diversified",
    body: "Spread across asset classes, so no single bet drives your outcome.",
  },
  {
    icon: RefreshCw,
    title: "Rebalanced for you",
    body: "Plutus keeps it on track automatically as markets and your life change.",
  },
  {
    icon: ShieldCheck,
    title: "Held in your name",
    body: "Your assets stay in your Interactive Brokers account. You're always the owner.",
  },
];

export interface AlphaWhyManagedModalProps {
  open: boolean;
  onClose: () => void;
  // Dashboard-only: seed the allocations editor at 100% matched managed.
  // Hidden when the account is already fully managed, the profile cannot
  // match, or the parent omitted the handler. Never auto-applies.
  showAdopt?: boolean;
  onAdopt?: (managedProductId: number) => void;
}

const formatOptionalPercentage = (value: number | null): string =>
  value == null ? "--" : formatPercentage(value, 1);

const formatOptionalSignedPercentage = (value: number | null): string =>
  value == null ? "--" : formatSignedPercentage(value, 1);

// Better = higher return / shallower (less negative) drawdown, and lower
// volatility. Only highlight when both figures are present.
const isBetter = (
  strategy: number | null,
  benchmark: number | null,
  lowerIsBetter: boolean
): boolean => {
  if (strategy == null || benchmark == null) return false;
  return lowerIsBetter ? strategy <= benchmark : strategy >= benchmark;
};

const AlphaWhyManagedModal: React.FC<AlphaWhyManagedModalProps> = ({
  open,
  onClose,
  showAdopt = false,
  onAdopt,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const vm = useAlphaWhyManagedData(open);
  const needsProfile = vm.status === "needsProfile";
  const showAdoptAction = Boolean(showAdopt && onAdopt) && !needsProfile;
  const adoptLocked = vm.managedStrategyId === null;

  const handleAdopt = () => {
    if (!onAdopt || !showAdoptAction || adoptLocked || vm.managedStrategyId === null) {
      return;
    }
    onAdopt(vm.managedStrategyId);
    onClose();
  };

  // Send the investor to update their saved risk profile (profile-update mode:
  // questions only, never the setup flow). Close first so we do not leave a
  // stale dialog behind the route change; return to wherever this modal opened.
  const handleUpdateRiskProfile = () => {
    onClose();
    openAlphaRiskProfileUpdate(
      navigate,
      location.state,
      location.pathname === "/alpha/profile" ? "/alpha/profile" : "/alpha/dashboard"
    );
  };

  const detailChart = useMemo(
    () => ({
      loading: vm.status === "loading",
      error: null,
      chartHeight: CHART_HEIGHT,
      chartData: vm.strategySeries,
      selectedPeriod: "All",
      entityType: "MANAGED_STRATEGY" as const,
    }),
    [vm.status, vm.strategySeries]
  );
  const providedBenchmarkSeries = useMemo(
    () => (ALPHA_BENCHMARK
      ? [{ benchmark: ALPHA_BENCHMARK, points: vm.benchmarkSeries }]
      : []),
    [vm.benchmarkSeries]
  );

  // Close on Escape while open (mirrors AlphaIbkrGuideModal).
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Move focus to the dialog heading on open so screen readers announce it.
  useEffect(() => {
    if (open) headingRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const tiles: ReadonlyArray<{ label: string; value: string; note: string; good: boolean }> = [
    {
      label: "Return YTD",
      value: formatOptionalSignedPercentage(vm.strategyReturnPct),
      note: `S&P ${formatOptionalSignedPercentage(vm.benchmarkReturnPct)}`,
      good: isBetter(vm.strategyReturnPct, vm.benchmarkReturnPct, false),
    },
    {
      label: "Max drawdown",
      value: formatOptionalPercentage(vm.maxDrawdown),
      note: `S&P ${formatOptionalPercentage(vm.benchmarkMaxDrawdown)}`,
      good: isBetter(vm.maxDrawdown, vm.benchmarkMaxDrawdown, false),
    },
    {
      label: "Volatility",
      value: formatOptionalPercentage(vm.volatility),
      note: `S&P ${formatOptionalPercentage(vm.benchmarkVolatility)}`,
      good: isBetter(vm.volatility, vm.benchmarkVolatility, true),
    },
  ];
  const recommendationContext = vm.managedStrategyId !== null ? (
    <div
      data-testid="alpha-why-managed-context"
      data-amount-source={vm.recommendationAmountSource ?? undefined}
      className="flex flex-wrap gap-2"
    >
      <span className="rounded-v7-control border border-v7-ink/12 bg-v7-paper px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-v7-ink-soft">
        Risk {vm.riskLabel}
      </span>
      <span className="rounded-v7-control border border-v7-ink/12 bg-v7-paper px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-v7-ink-soft">
        {vm.accountTypeLabel} account
      </span>
      <span className="rounded-v7-control border border-v7-ink/12 bg-v7-paper px-2.5 py-1 font-mono text-[11px] font-semibold text-v7-ink">
        {vm.recommendationAmountUsd === null
          ? "--"
          : formatCurrencyWithCommas(vm.recommendationAmountUsd, 0)}
      </span>
    </div>
  ) : null;
  const valueProposition = (
    <ul
      data-testid="alpha-why-managed-value-proposition"
      className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-1"
    >
      {WHY_REASONS.map((reason) => {
        const Icon = reason.icon;
        return (
          <li
            key={reason.title}
            className="grid grid-cols-[20px_minmax(0,1fr)] gap-3 rounded-v7-control border border-v7-ink/12 bg-v7-white p-3"
          >
            <Icon className="mt-0.5 h-[18px] w-[18px] text-v7-bronze-dark" aria-hidden />
            <div>
              <p className="text-[13px] font-semibold text-v7-ink">{reason.title}</p>
              <p className="mt-0.5 text-xs leading-snug text-v7-ink-soft">{reason.body}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex animate-[alphaOverlayIn_0.2s_ease-out] bg-v7-ink/60 motion-reduce:animate-none sm:items-center sm:justify-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alpha-why-managed-title"
        data-testid="alpha-why-managed-modal"
        className="flex h-full w-full flex-col overflow-hidden bg-v7-white animate-[alphaModalIn_0.24s_cubic-bezier(0.4,0,0.2,1)] motion-reduce:animate-none sm:h-auto sm:max-h-[92vh] sm:w-[92vw] sm:max-w-[860px] sm:rounded-v7-panel sm:border sm:border-v7-ink/10 sm:shadow-[0_24px_60px_rgba(17,29,50,0.35)]"
      >
        <div className="flex items-center justify-between gap-4 border-b border-v7-ink/12 px-5 pb-4 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-6 sm:pt-5">
          <h2
            ref={headingRef}
            tabIndex={-1}
            id="alpha-why-managed-title"
            className="font-v7-serif text-2xl font-normal tracking-[-0.01em] text-v7-ink focus:outline-none sm:text-[28px]"
          >
            Go 100% Plutus Managed
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-v7-control border border-v7-ink/15 text-v7-ink-soft transition-colors hover:border-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6" aria-live="polite">
          {vm.status === "loading" ? (
            <div
              role="status"
              className="flex items-center justify-center gap-2 py-20 text-sm text-v7-ink-soft"
            >
              <LoaderCircle className="h-5 w-5 animate-spin text-v7-bronze-dark" aria-hidden="true" />
              Loading recommendation
            </div>
          ) : (
            <>
              {needsProfile ? (
                <div
                  data-testid="alpha-why-managed-risk-prompt"
                  className="flex flex-wrap items-center gap-3 rounded-v7-control border border-v7-bronze-dark/30 bg-v7-paper px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-v7-ink">
                      Add your risk information
                    </p>
                    <p className="mt-0.5 text-xs text-v7-ink-soft">
                      Complete your risk information to see this recommendation.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleUpdateRiskProfile}
                    className="shrink-0 rounded-v7-control bg-v7-ink px-3 py-2 text-[13px] font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
                  >
                    Update risk information
                  </button>
                </div>
              ) : recommendationContext}

              {!needsProfile && vm.status === "ready" ? (
                <>
                  <div
                    data-testid="alpha-why-managed-responsive-content"
                    className="mt-5 grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start"
                  >
                    <section
                      className="overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white"
                      aria-labelledby="alpha-why-managed-performance-title"
                      data-testid="alpha-why-managed-performance"
                    >
                      <h3
                        id="alpha-why-managed-performance-title"
                        className="px-4 pt-4 font-v7-serif text-lg font-normal text-v7-ink"
                      >
                        How this strategy would have performed
                      </h3>
                      <div className="px-2 pb-3 pt-1">
                        <DetailNetBenchmarkChart
                          chart={detailChart}
                          benchmarks={ALPHA_BENCHMARK ? [ALPHA_BENCHMARK] : []}
                          providedBenchmarkSeries={providedBenchmarkSeries}
                          compact
                          showNet={false}
                          showPeriodFilters={false}
                          showWatermark={false}
                          isDarkMode={false}
                          useUtcDateLabels
                          productLabel={vm.productLabel}
                          ariaLabel={`${vm.productLabel} year-to-date cumulative return ${formatOptionalSignedPercentage(vm.strategyReturnPct)} versus the S&P 500 ${formatOptionalSignedPercentage(vm.benchmarkReturnPct)}`}
                        />
                      </div>
                    </section>

                    {valueProposition}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-px overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-ink/10 sm:grid-cols-3">
                    {tiles.map((tile) => (
                      <div key={tile.label} className="bg-v7-white p-3">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.07em] text-v7-ink/50">
                          {tile.label}
                        </p>
                        <p className="mt-1 font-v7-serif text-lg text-v7-ink">{tile.value}</p>
                        <p className={`mt-0.5 text-[10px] ${tile.good ? "text-v7-success" : "text-v7-ink-soft"}`}>
                          {tile.note}
                        </p>
                      </div>
                    ))}
                  </div>

                  <section
                    data-testid="alpha-why-managed-constituents"
                    aria-labelledby="alpha-why-managed-constituents-title"
                    className="mt-6 border-t border-v7-ink/12 pt-4"
                  >
                    <h3
                      id="alpha-why-managed-constituents-title"
                      className="text-[13px] font-semibold text-v7-ink-soft"
                    >
                      Constituent mix breakdown
                    </h3>
                    <ul className="mt-2 divide-y divide-v7-ink/10 rounded-v7-control bg-v7-paper px-4 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:divide-y-0">
                      {vm.constituents.map((constituent) => (
                        <li
                          key={constituent.name}
                          className="flex items-center justify-between gap-4 border-b border-v7-ink/10 py-2.5 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0"
                        >
                          <span className="min-w-0 text-[13px] text-v7-ink-soft">
                            {constituent.name}
                          </span>
                          <span className="shrink-0 font-mono text-[13px] text-v7-ink">
                            {formatPercentage(
                              constituent.weightPct,
                              Number.isInteger(constituent.weightPct) ? 0 : 1
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                </>
              ) : (
                <>
                  {!needsProfile && (
                    <p className="mt-5 rounded-v7-control border border-v7-ink/15 bg-v7-paper px-4 py-3 text-sm text-v7-ink-soft">
                      {vm.managedStrategyId !== null
                        ? "Performance and constituent details are not available right now. You can still adopt this recommendation."
                        : "Recommendation details are not available right now. Try again later."}
                    </p>
                  )}
                  <div className="mt-5">{valueProposition}</div>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-v7-ink/12 px-5 py-4 sm:px-6">
          <p className="text-[11px] text-v7-ink-soft">
            Built by <span className="font-semibold text-v7-ink">Argento</span>, our in-house
            model &middot; overseen by Plutus
          </p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className={showAdoptAction
                ? "shrink-0 rounded-v7-control border border-v7-ink/25 bg-v7-white px-4 py-2 text-sm font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
                : "shrink-0 rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"}
            >
              Got it
            </button>
            {showAdoptAction && (
              <button
                type="button"
                onClick={handleAdopt}
                disabled={adoptLocked}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
              >
                {vm.status === "loading" && vm.managedStrategyId === null && (
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                )}
                Adopt
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlphaWhyManagedModal;
